import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ApiService from '../services/api';

const CompetitorSelector = ({ onCompetitorSelected, onManageCompetitors, onShowAnalysis }) => {
  const { user } = useAuth();
  const [competitors, setCompetitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadCompetitors();
  }, [user]);

  const loadCompetitors = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);
      const response = await ApiService.getSharedCompetitors(user.user_id);
      
      if (response.success) {
        // Combine owned and shared competitors for selection
        const allCompetitors = [
          ...(response.competitors?.owned || []),
          ...(response.competitors?.shared || [])
        ];
        setCompetitors(allCompetitors);
      } else {
        setError(response.error || 'Failed to load competitors');
      }
    } catch (err) {
      setError('Failed to load competitors: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCompetitorClick = (competitor) => {
    onCompetitorSelected(competitor);
  };

  const handleCompetitorNameClick = (event, competitor) => {
    event.stopPropagation(); // Prevent the card click event
    onShowAnalysis(competitor);
  };

  if (loading) {
    return (
      <div className="card">
        <div className="loading">Loading competitors...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="error">
          <h3>Error</h3>
          <p>{error}</p>
          <button 
            onClick={loadCompetitors}
            className="primary-button"
            style={{ marginTop: '10px' }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <h2 className="card-title">Select Competitor</h2>
      <p className="card-subtitle">Choose who will practice spelling bee words</p>

      {competitors.length === 0 ? (
        <div className="empty-state">
          <p>No competitors yet!</p>
          {error && (
            <div style={{ color: 'red', fontSize: '0.9rem', marginBottom: '10px' }}>
              Error: {error}
            </div>
          )}
          <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '20px' }}>
            Create your first competitor to start practicing.
          </p>
          <button 
            onClick={onManageCompetitors}
            className="primary-button"
          >
            Create Competitor
          </button>
        </div>
      ) : (
        <div>
          <div className="competitors-grid">
            {competitors.map((competitor) => (
              <div
                key={competitor.competitor_id}
                className="competitor-card"
                onClick={() => handleCompetitorClick(competitor)}
              >
                <div className="competitor-avatar">
                  {competitor.name.charAt(0).toUpperCase()}
                </div>
                <h3
                  className="competitor-name-link"
                  onClick={(e) => handleCompetitorNameClick(e, competitor)}
                >
                  {competitor.name}
                </h3>
                <p className="competitor-created">
                  Created {new Date(competitor.created_at).toLocaleDateString()}
                </p>
              </div>
            ))}
          </div>

          <div className="actions" style={{ marginTop: '20px', textAlign: 'center' }}>
            <button 
              onClick={onManageCompetitors}
              className="secondary-button"
            >
              Manage Competitors
            </button>
          </div>
        </div>
      )}

      <style jsx>{`
        .competitors-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 15px;
          margin: 20px 0;
        }

        .competitor-card {
          background: #f8f9fa;
          border: 2px solid #e9ecef;
          border-radius: 8px;
          padding: 20px;
          text-align: center;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .competitor-card:hover {
          border-color: #007bff;
          background: #e3f2fd;
          transform: translateY(-2px);
        }

        .competitor-avatar {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          background: #007bff;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          font-weight: bold;
          margin: 0 auto 15px;
        }

        .competitor-card h3 {
          margin: 0 0 8px 0;
          color: #333;
          font-size: 1.1rem;
        }

        .competitor-name-link {
          color: #007bff;
          cursor: pointer;
          text-decoration: underline;
          transition: color 0.2s;
        }

        .competitor-name-link:hover {
          color: #0056b3;
        }

        .competitor-created {
          margin: 0;
          color: #666;
          font-size: 0.85rem;
        }

        .empty-state {
          text-align: center;
          padding: 40px 20px;
          color: #666;
        }

        .empty-state p:first-child {
          font-size: 1.2rem;
          color: #333;
          margin-bottom: 10px;
        }

        .primary-button {
          background-color: #007bff;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 1rem;
          transition: background-color 0.2s;
        }

        .primary-button:hover {
          background-color: #0056b3;
        }

        .secondary-button {
          background-color: #6c757d;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.9rem;
          transition: background-color 0.2s;
        }

        .secondary-button:hover {
          background-color: #545b62;
        }

        .error {
          text-align: center;
          color: #dc3545;
        }

        .loading {
          text-align: center;
          padding: 40px;
          color: #666;
        }
      `}</style>
    </div>
  );
};

export default CompetitorSelector;